<script lang="ts" setup>
import PlugImage from "~/assets/icons/search/product-plug-unsized.svg"
import ArrowIcon from "~/assets/icons/arrows/24/arrow-left.svg"
import ProductSlider from "~/widgets/search/product/components/ProductSlider.vue"
import ProductBuy from "~/widgets/search/product/components/ProductBuy.vue"
import ProductInfo from "~/widgets/search/product/components/ProductInfo.vue"
import BuyOneClickModal from "~/widgets/search/product/components/BuyOneClickModal.vue"
import useSearchStore from "~/shared/store/modules/search"
import SuccessModal from "~/entities/search/modals/SuccessModal.vue"
import RememberMeModal from "~/widgets/search/product/components/RememberMeModal.vue"
import MainTitle from "~/components/ui/title/MainTitle.vue"
import type {IProductInfo} from "~/shared/interfaces/search/product"

const props = defineProps<{
  product: IProductInfo,
  minPrice: number | null
}>()

const productTitle = ref<HTMLTitleElement>()
const productBuyPadding = computed(() => productTitle.value ? `${productTitle.value?.offsetHeight + 24}px` : null)
const searchStore = useSearchStore()
const router = useRouter()


const successModalConfig = {
  oneClick: {
    title: 'Заявка успешно отправлена',
    text: 'Наш менеджер свяжется с вами для уточнения деталей'
  },
  rememberMe: {
    title: 'Подписка на товар оформлена',
    text: `На ваш email ${searchStore.rememberMeModalEmail} придет письмо, когда товар появится в наличии`,
  }
}

function goToPreviousPage() {
  if (router.options.history.state.back) {
    return router.go(-1);
  }
  return router.push('/');
}

useJsonld({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: props.product.title,
  image: props.product.photos[0]?.url || "",
  description: props.product.description,
  model: props.product.article,
  offers: {
    '@type': "AggregateOffer",
    priceCurrency: "RUB",
    availability: "https://schema.org/InStock",
    lowPrice: props.minPrice || 0,
  },
  brand: {
      '@type': "Brand",
      name: props.product.brand.name
  }
}, {tagPosition: "head"});

</script>

<template>
  <section
    class="product"
    v-if="product"
  >
    <div class="container">
      <MainTitle class="product__title" ref="productTitle" :text="product.title" size="s"/>
      <div class="product__container">
          <div v-if="!product.photos.length" class="product__container-button">
              <button class="product__container-button-arrow-icon" @click="goToPreviousPage">
                <ArrowIcon/>
              </button>
          </div>
          <ProductSlider
          v-if="product.photos.length"
          :slides="product.photos"
            />
          <div
            v-else
            class="product__default-image"
          >
            <PlugImage/>
          </div>
          <ProductInfo
            :product="product"
            :min-price="minPrice"
          />
          <ProductBuy
            class="product-buy_desktop"
            v-if="productBuyPadding"
            :padding-top="productBuyPadding"
            :product="product"
            :min-price="minPrice"
            @open-modal="searchStore.setOneClickModalState(true)"
          />
      </div>

      <ClientOnly>
        <Teleport to="body">
          <BuyOneClickModal
            v-if="product && minPrice"
            :product="product"
            :min-price="minPrice"
            @close-modal="searchStore.setOneClickModalState(false)"
          />
          <RememberMeModal
            v-if="!minPrice"
            :product="product"
            @close-modal="searchStore.setRememberMeModalState(false)"
          />
          <SuccessModal
            :modal-title="successModalConfig[searchStore.successModalType].title"
            :modal-text="successModalConfig[searchStore.successModalType].text"
          />
        </Teleport>
      </ClientOnly>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.product {
  padding-top: 28px;
  @include md-breakpoint {
    padding: 0 0 28px;
    background-color: $bg_page;
    border-radius: 0 0 12px 12px;
  }

  .container {
    display: flex;
    flex-direction: column;
    gap: 16px;
    @include md-breakpoint {
      flex-direction: column;
      padding: 0;
    }
  }

  &__container {
    position: relative;
    display: flex;
    justify-content: space-between;
    width: 100%;
    gap: 24px;
    @include md-breakpoint {
      max-width: none;
      flex-wrap: nowrap;
      flex-direction: column;
      gap: 0;
    }

    &-button {
      position: absolute;
      width: 100%;
      display: none;
      justify-content: center;
      align-items: center;
      background-color: $control-bg_white;
      box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.0784313725);
      border-radius: 50%;
      width: 40px;
      height: 40px;
      top: 12px;
      left: 20px;
      z-index: 5;

      @include md-breakpoint {
        display: flex;
      }

      &-arrow-icon {
      display: flex;
      width: 24px;
      height: 24px;
     }
    }
  }

  &__default-image {
    position: relative;
    display: flex;
    @include md-breakpoint {
      margin: 0 auto;
      order: -1;

      &:before, &:after {
        content: '';
        background-color: rgba(98, 98, 98, .05);
        display: block;
        position: absolute;
        top: 0;
        width: 100%;
        height: 100%;
      }

      &:before {
        right: 100%;
      }

      &:after {
        left: 100%;
      }
    }

    @include xs-breakpoint {
      width: 360px;
      height: 360px;
    }

    svg {
      width: 432px;
      height: 432px;
      @include md-breakpoint {
        width: 100%;
        height: 360px;
      }
    }
  }

  &__title {
    width: 100%;
    @include md-breakpoint {
      @include font(24px, 28px, 600);
      padding: 0 20px;
      margin-top: 12px;
    }
  }
}

.product-buy_desktop {
  @include md-breakpoint {
    display: none;
  }
}

.product-slider {
  @include md-breakpoint {
    order: -1;
  }
}
</style>
